import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Popover } from "../ui/Popover";
import { anchorStyle } from "./anchor";
import { cn } from "../ui/cn";
import { PencilIcon } from "../ui/icons";
import type { CannedKind, CannedResponse } from "../../types";

/** Snippets, cached hard -- the library changes rarely and is read constantly. */
export function useCannedResponses() {
  return useQuery({
    queryKey: queryKeys.canned,
    queryFn: api.cannedResponses,
    staleTime: 5 * 60_000,
  });
}

/**
 * Match a query against a snippet's shortcut, title and category.
 *
 * Shortcut matches rank first: somebody typing "ret" who has a `#returns`
 * shortcut means that snippet, and burying it under a title that happens to
 * contain the letters would make the shortcut feel unreliable.
 *
 * `kind` is required rather than defaulted, and that is deliberate. Every
 * caller is a specific box -- the reply editor or a subject line -- and there
 * is no sensible "either": offering a subject template in the body editor
 * inserts a bare line of text where a paragraph was expected, and offering a
 * reply snippet in a subject box inserts markup into a mail header. A default
 * would make forgetting to pass it silent, which is exactly the bug. It is
 * also what makes a shortcut unambiguous despite being unique only per kind.
 */
export function filterCanned(
  responses: CannedResponse[],
  query: string,
  kind: CannedKind,
): CannedResponse[] {
  const needle = query.trim().toLowerCase();
  const active = responses.filter(
    (entry) => entry.isActive && entry.kind === kind,
  );
  if (!needle) return active;

  const scored = active
    .map((entry) => {
      const shortcut = entry.shortcut?.toLowerCase() ?? "";
      const title = entry.title.toLowerCase();
      const category = entry.category?.toLowerCase() ?? "";

      if (shortcut === needle) return { entry, score: 0 };
      if (shortcut.startsWith(needle)) return { entry, score: 1 };
      if (title.startsWith(needle)) return { entry, score: 2 };
      if (title.includes(needle)) return { entry, score: 3 };
      if (category.includes(needle)) return { entry, score: 4 };
      return { entry, score: -1 };
    })
    .filter((row) => row.score >= 0)
    .sort((a, b) => a.score - b.score);

  return scored.map((row) => row.entry);
}

/**
 * The toolbar's canned-response menu.
 *
 * Kept to the size of an attachment picker: a short scrollable list with a
 * search box, not a dialog. Choosing a snippet is a step inside writing a
 * reply, and a full-screen overlay for it hides the very thing being written.
 */
export function CannedPicker({
  onInsert,
}: {
  onInsert: (response: CannedResponse) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data } = useCannedResponses();

  const matches = useMemo(
    /* The toolbar button lives in the reply editor, so it only ever offers
       bodies. Subject templates are reached from the subject box. */
    () => filterCanned(data ?? [], query, "body"),
    [data, query],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      label="Insert canned response"
      trigger={
        <span
          title="Insert canned response"
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-md border border-line text-muted",
            "hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
            open && "bg-surface-2 text-content",
          )}
        >
          <PencilIcon />
        </span>
      }
    >
      <div className="w-72">
        <div className="border-b border-line p-2">
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search responses…"
            className={cn(
              "w-full rounded border border-line bg-surface px-2 py-1 text-xs text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          />
        </div>

        <ul className="max-h-64 overflow-y-auto p-1">
          {matches.length === 0 ? (
            <li className="px-2 py-3 text-center text-xs text-muted">
              {data?.length ? "No matches." : "No canned responses yet."}
            </li>
          ) : (
            matches.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => {
                    onInsert(entry);
                    setOpen(false);
                    setQuery("");
                  }}
                  className="w-full rounded px-2 py-1.5 text-left hover:bg-surface-2"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-xs text-content">
                      {entry.title}
                    </span>
                    {entry.shortcut ? (
                      <span className="shrink-0 rounded bg-surface-2 px-1 text-[10px] text-muted-on-tint">
                        #{entry.shortcut}
                      </span>
                    ) : null}
                  </div>
                  <div className="truncate text-[11px] text-faint">
                    {toPreview(entry.bodyHtml)}
                  </div>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </Popover>
  );
}

/**
 * A one-line plain-text preview of a snippet.
 *
 * Uses the DOM parser rather than a regex: stripping tags by pattern is the
 * classic way to mangle content that merely contains a "<".
 *
 * Each block and list item is closed with a space first. `textContent` alone
 * runs them together, so a list of three things previewed as "The order
 * numberThe item(s)…" -- which looked like the snippet itself was squashed.
 */
export function toPreview(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;
  for (const block of host.querySelectorAll("p, div, li, br")) {
    block.after(" ");
  }
  return (host.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * The inline `#shortcut` autocomplete shown under the caret.
 *
 * Separate from the toolbar picker because it is a different interaction: no
 * search box (the typed text *is* the query) and it must sit at the caret
 * rather than at a fixed anchor.
 */
/** Matches the `max-h-52` on the list below, in px. */
const MENU_MAX_HEIGHT = 208;

export function CannedInlineMenu({
  matches,
  activeIndex,
  position,
  onPick,
}: {
  matches: CannedResponse[];
  activeIndex: number;
  position: { top: number; left: number };
  onPick: (response: CannedResponse) => void;
}) {
  if (matches.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Canned responses"
      style={anchorStyle(position.top, position.left, 256, MENU_MAX_HEIGHT)}
      className="fixed z-40 overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
    >
      <ul className="max-h-52 overflow-y-auto p-1">
        {matches.map((entry, index) => (
          <li key={entry.id}>
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              // onMouseDown, not onClick: clicking would blur the editor first
              // and the caret position the insert depends on would be gone.
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(entry);
              }}
              className={cn(
                "w-full rounded px-2 py-1.5 text-left",
                index === activeIndex ? "bg-surface-2" : "hover:bg-surface-2",
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-xs text-content">
                  {entry.title}
                </span>
                {entry.shortcut ? (
                  <span className="shrink-0 text-[10px] text-muted">
                    #{entry.shortcut}
                  </span>
                ) : null}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
